import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ConfirmPaymentRequestBody,
  TransactionStatus,
  WalletsPaymentsClient,
} from "@nestcoinco/hyper-api-gateway-api-client";
import apiConfig from "../../config/api-config";
import { ROUTES } from "../../utils/constants";
import { usePaymentConfirmationContext } from "../../components/Payments/PaymentConfirmationProvider";
import PaymentStatus, {
  PaymentStatusType,
} from "../../components/Payments/PaymentStatus/PaymentStatus";
import Logger from "../../utils/logger";

enum PaymentConfirmationUrlParams {
  REFERENCE = "reference",
  PAYER_ID = "PayerID",
  TOKEN = "token",
  PAYMENT_ID = "paymentId",
}

function PaymentConfirmation() {
  const navigate = useNavigate();
  const { provider } = useParams();
  const [queryParams] = useSearchParams();
  const { updateTopupAmount } = usePaymentConfirmationContext();

  const confirmPayment = useCallback(
    async (provider: string, paymentParams: ConfirmPaymentRequestBody) => {
      const paymentClient = new WalletsPaymentsClient(apiConfig);
      try {
        const paymentResponse = (
          await paymentClient.confirmPayment(provider, paymentParams)
        ).data;

        if (paymentResponse.success) {
          const route = paymentResponse.isFirstTime
            ? ROUTES.PAYMENTS.FIRST_TOPUP_SUCCESSFUL
            : ROUTES.PAYMENTS.RETURN_TOPUP_SUCCESSFUL;
          updateTopupAmount(paymentResponse.amount);
          navigate(route);
          return;
        }

        if (
          paymentResponse.status === TransactionStatus.REJECTED ||
          paymentResponse.status === TransactionStatus.FAILED
        ) {
          navigate(ROUTES.PAYMENTS.FAILED_TOPUP);
          return;
        }

        navigate(ROUTES.PAYMENTS.PENDING_TOPUP);
      } catch (e) {
        Logger.log(e);
        navigate(ROUTES.PAYMENTS.PENDING_TOPUP);
      }
    },
    [navigate, updateTopupAmount]
  );

  useEffect(() => {
    const getPaymentParams = () => {
      const reference =
        queryParams.get(PaymentConfirmationUrlParams.REFERENCE) || "";
      const payerId =
        queryParams.get(PaymentConfirmationUrlParams.PAYER_ID) ?? undefined;
      const token =
        queryParams.get(PaymentConfirmationUrlParams.TOKEN) ?? undefined;
      const paymentId =
        queryParams.get(PaymentConfirmationUrlParams.PAYMENT_ID) ?? undefined;
      return { reference, payerId, token, paymentId };
    };

    const paymentParams = getPaymentParams();
    confirmPayment(provider as string, paymentParams);
  }, [provider, queryParams, confirmPayment]);

  return (
    <PaymentStatus
      type={PaymentStatusType.LOADING}
      title="Confirming Payment"
      message="Your payment is being confirmed..."
    />
  );
}

export default PaymentConfirmation;
